/**
 * Workload TECHNICAL + BTM-POWER reference — the cited engineering datapoints
 * behind each `WorkloadProfile`, and the reverse-direction map of how a chosen
 * behind-the-meter (BTM) power source constrains the workload it can serve.
 *
 * This is the SINGLE SOURCE the intent table, the Ask-DAM context, the SED
 * grounding block, and the Feasibility / Power report sections all read from —
 * so every surface quotes the same numbers with the same citations.
 *
 * ⚠️ EVERY quantitative value here is REFERENCED, not estimated. Each field
 * carries a `*Source` id into `SOURCES` (rendered as a superscript marker in the
 * UI and as a numbered footnote in reports). Where a figure could not be sourced
 * cleanly (e.g. gas / large-nuclear LCOE, which are gas-price / capex dependent),
 * the cell says so rather than inventing a number — per the project rule
 * "never guess engineering values without stating the assumption and its source."
 *
 * These are REFERENCE / DISPLAY datapoints. They do NOT feed scoring, LCOE, or
 * any computed report number — a site's real figures come from its
 * `FinancialAssumptions` and the power-stack resolver.
 */
import type { WorkloadProfile } from './workloadProfile';

/* ── Sources ──────────────────────────────────────────────────────────────
 * Numbered registry. `n` is the stable footnote marker — do NOT reorder, the
 * numbers are quoted across intent / DAM / SED / reports. */
export type SourceId =
  | 'uptime2024'
  | 'nvidiaGb200'
  | 'introlCooling'
  | 'inferenceVsTraining'
  | 'aiPowerVolatility'
  | 'eiaCapacityFactor'
  | 'smrNuclear'
  | 'btmGas'
  | 'lbnl2024';

export interface ReferenceSource {
  /** Stable footnote number rendered as a superscript [n]. */
  n: number;
  title: string;
  org: string;
  year: number;
  url: string;
  /** The specific figure(s) this source backs. */
  backs: string;
}

export const SOURCES: Record<SourceId, ReferenceSource> = {
  uptime2024: {
    n: 1,
    title: 'Global Data Center Survey 2024',
    org: 'Uptime Institute',
    year: 2024,
    url: 'https://uptimeinstitute.com/resources/research-and-reports/uptime-institute-global-data-center-survey-results-2024',
    backs: 'Global average PUE ≈1.56 (flat 5 yrs); average rack density ≈8 kW, majority of racks <30 kW.',
  },
  nvidiaGb200: {
    n: 2,
    title: 'GB200 NVL72 rack specification',
    org: 'NVIDIA / HPE',
    year: 2025,
    url: 'https://www.nvidia.com/en-us/data-center/gb200-nvl72/',
    backs: 'GB200 NVL72 ≈120 kW/rack nominal (NVIDIA); ≈132 kW deployed (HPE: 115 kW liquid + 17 kW air).',
  },
  introlCooling: {
    n: 3,
    title: 'Liquid Cooling vs Air: the 50 kW GPU Rack Guide (2025)',
    org: 'Introl',
    year: 2025,
    url: 'https://introl.com/blog/liquid-cooling-gpu-data-centers-50kw-thermal-limits-guide',
    backs: 'Air cooling practical limit ≈40 kW/rack, ≈50 kW inflection; rear-door HX ≤40 kW; direct-to-chip removes 70–75% of rack heat (partial PUE 1.02–1.03); single-phase immersion ≈200 kW/rack.',
  },
  inferenceVsTraining: {
    n: 4,
    title: 'AI inference vs AI training data-center infrastructure; the next big shifts in AI workloads',
    org: 'Digital Edge / McKinsey',
    year: 2025,
    url: 'https://id.digitaledgedc.com/ai-infrastructure/ai-inference-vs-ai-training',
    backs: 'Training racks 40–140 kW (frontier → ~1 MW); inference 10–30 kW/rack, bursty/diurnal; inference accounts for approximately two-thirds of AI compute by 2026 and ~80% of AI IT load by 2030.',
  },
  aiPowerVolatility: {
    n: 5,
    title: 'Neutralizing the gigascale problem: the physical power paradox of extreme AI training loads',
    org: 'IEEE Spectrum / SemiAnalysis',
    year: 2025,
    url: 'https://spectrum.ieee.org/gigascale-ai-datacenter-power',
    backs: 'Synchronized GPU training jobs create high-frequency, abrupt power swings → transient voltage/frequency risk; BESS + supercapacitor firming decouples GPU load from the grid and firms renewables.',
  },
  eiaCapacityFactor: {
    n: 6,
    title: 'Electric Power Monthly, Tables 6.07.A (Fossil Fuels) & 6.07.B (Non-Fossil Fuels) — capacity factors (2024)',
    org: 'U.S. Energy Information Administration',
    year: 2024,
    url: 'https://www.eia.gov/electricity/monthly/epm_table_grapher.php?t=epmt_6_07_a',
    backs: 'Annual capacity factors: natural-gas combined cycle ≈60%, nuclear ≈91%, wind ≈34%, solar PV ≈23%.',
  },
  smrNuclear: {
    n: 7,
    title: 'SMR nuclear power for AI data centers: feasibility & timeline',
    org: 'Introl / NuScale',
    year: 2025,
    url: 'https://introl.com/blog/smr-nuclear-power-ai-data-centers-implementation',
    backs: 'SMR capacity factor 95%+, generally post-2030; interconnection queues averaging >2,100 days (secondary blog LCOE ranges ≈$89–102/MWh SMR and ≈$26–50/MWh pre-firming renewables are omitted from site economic basis pending primary-source table/page, dollar year, and 24/7 firming/interconnection boundary).',
  },
  btmGas: {
    n: 8,
    title: 'Why data centers are looking to natural gas for behind-the-meter power',
    org: 'Enverus / CoreSite',
    year: 2025,
    url: 'https://www.enverus.com/blog/why-data-centers-are-looking-to-natural-gas-for-behind-the-meter-power/',
    backs: 'BTM natural gas (recip / turbine) gives continuous dispatchable baseload with short deployment timelines; multi-GW co-located gas + data-center plants emerging (Chevron + GE Vernova).',
  },
  lbnl2024: {
    n: 9,
    title: '2024 United States Data Center Energy Usage Report',
    org: 'Lawrence Berkeley National Laboratory (DOE)',
    year: 2024,
    url: 'https://eta.lbl.gov/publications/2024-lbnl-data-center-energy-usage-report',
    backs: 'US data centers used 176 TWh in 2023 (4.4% of US electricity); projected 325–580 TWh (6.7–12%) by 2028, driven largely by AI.',
  },
};

/** Ordered list for rendering a numbered "Sources" footer. */
export const SOURCES_ORDERED: ReadonlyArray<ReferenceSource> = Object.values(SOURCES).sort((a, b) => a.n - b.n);

/** One-line macro framing, cited — useful as a report/DAM lede. */
export const DATACENTER_POWER_MACRO = {
  text: 'US data centers used 176 TWh in 2023 (4.4% of US electricity) and are projected to reach 325–580 TWh (6.7–12%) by 2028, driven largely by AI.',
  source: 'lbnl2024' as SourceId,
};

/* ── Per-workload technical reference ────────────────────────────────────── */

export interface WorkloadTechRef {
  /** Rack power density, kW/rack. */
  rackDensityKw: string;
  rackDensitySource: SourceId;
  /** Cooling technology the density forces. */
  coolingRequired: string;
  coolingSource: SourceId;
  /** Typical / achievable PUE. */
  typicalPue: string;
  pueSource: SourceId;
  /** Load factor + power-quality (volatility) shape. */
  powerProfile: string;
  powerProfileSource: SourceId;
  /** How the workload constrains the BTM power source (see BTM_SOURCE_REFERENCE for the reverse). */
  btmFit: string;
  btmFitSource: SourceId;
}

export const WORKLOAD_TECH_REFERENCE: Record<WorkloadProfile, WorkloadTechRef> = {
  'ai-training': {
    rackDensityKw: '40–140 (GB200 NVL72 ≈120–132); frontier → ~1 MW/rack',
    rackDensitySource: 'nvidiaGb200',
    coolingRequired: 'Indicative envelope (conditional on OEM/thermal design; project selection not established): direct-to-chip liquid typically adopted above ~40–50 kW/rack (conventional air practical limit); immersion candidate for >150 kW/rack',
    coolingSource: 'introlCooling',
    typicalPue: '≈1.2 (hyperscale; DLC partial-PUE 1.02–1.03)',
    pueSource: 'introlCooling',
    powerProfile: 'Sustained high load factor; synchronized GPU swings drive high-frequency volatility (evaluate BESS/supercapacitor smoothing in transient study)',
    powerProfileSource: 'aiPowerVolatility',
    btmFit: 'Needs dispatchable power at high load factor: gas (≈60% historical fleet CF, distinct from dedicated unit availability; site N-1/ELCC design, firm fuel lateral & air permit required), SMR post-2030 (CF 95%+); solar/wind (CF 23–34%) only if heavily overbuilt + BESS',
    btmFitSource: 'eiaCapacityFactor',
  },
  'ai-inference': {
    rackDensityKw: '10–30 typical (large inference to ~150)',
    rackDensitySource: 'inferenceVsTraining',
    coolingRequired: 'High-end air or direct-to-chip (10–30 kW band)',
    coolingSource: 'introlCooling',
    typicalPue: '≈1.2–1.4 (metro, mixed cooling)',
    pueSource: 'uptime2024',
    powerProfile: '24/7 serving; bursty diurnal per rack; inference accounts for approximately two-thirds of AI compute by 2026',
    powerProfileSource: 'inferenceVsTraining',
    btmFit: 'Flexibility suits grid + renewables + BESS; more demand-response tolerant than training',
    btmFitSource: 'eiaCapacityFactor',
  },
  hpc: {
    rackDensityKw: '30–50+ (training-class, batch)',
    rackDensitySource: 'inferenceVsTraining',
    coolingRequired: 'Direct-to-chip / rear-door HX (>40 kW)',
    coolingSource: 'introlCooling',
    typicalPue: '≈1.2–1.4',
    pueSource: 'uptime2024',
    powerProfile: 'Batch-scheduled, time-of-use tolerant (can ride off-peak)',
    powerProfileSource: 'inferenceVsTraining',
    btmFit: 'Batch tolerance fits intermittent renewables + storage; can chase cheap off-peak power',
    btmFitSource: 'eiaCapacityFactor',
  },
  enterprise: {
    rackDensityKw: '≈8 average; majority <30',
    rackDensitySource: 'uptime2024',
    coolingRequired: 'Contained air sufficient (<30 kW)',
    coolingSource: 'introlCooling',
    typicalPue: '≈1.56 (global average)',
    pueSource: 'uptime2024',
    powerProfile: 'Steady commodity load',
    powerProfileSource: 'uptime2024',
    btmFit: 'Grid-primary; modest BTM / BESS',
    btmFitSource: 'eiaCapacityFactor',
  },
  colocation: {
    rackDensityKw: '5–30 mixed; wholesale trending higher',
    rackDensitySource: 'uptime2024',
    coolingRequired: 'Air baseline; direct-to-chip-ready for dense / AI tenants',
    coolingSource: 'introlCooling',
    typicalPue: '≈1.3–1.6',
    pueSource: 'uptime2024',
    powerProfile: 'Mixed tenant loads; ramps with occupancy',
    powerProfileSource: 'uptime2024',
    btmFit: 'Tenant SLAs demand firm N+1: gas / grid + BESS; SMR at wholesale scale',
    btmFitSource: 'btmGas',
  },
  edge: {
    rackDensityKw: '5–10',
    rackDensitySource: 'uptime2024',
    coolingRequired: 'Air / free cooling',
    coolingSource: 'introlCooling',
    typicalPue: '≈1.4–1.6 (small-site)',
    pueSource: 'uptime2024',
    powerProfile: 'Low, latency-driven, often unmanned',
    powerProfileSource: 'uptime2024',
    btmFit: 'Grid + small BESS; footprint-constrained',
    btmFitSource: 'eiaCapacityFactor',
  },
  mixed: {
    rackDensityKw: '10–30 (enterprise default until firmed)',
    rackDensitySource: 'uptime2024',
    coolingRequired: 'Air default; direct-to-chip if densified',
    coolingSource: 'introlCooling',
    typicalPue: '≈1.5 (global average)',
    pueSource: 'uptime2024',
    powerProfile: 'Enterprise-grade assumed until workload firmed',
    powerProfileSource: 'uptime2024',
    btmFit: 'Firm up the workload first — it sets the required capacity factor and firmness',
    btmFitSource: 'eiaCapacityFactor',
  },
};

/* ── BTM power-source reference (the reverse direction) ──────────────────────
 * How each behind-the-meter source's capacity factor / lead time / dispatch-
 * ability bounds the workload it can serve at a given load factor. Aligns with
 * the intent form's phase power components (recip / gas-turbine / battery / grid
 * / solar-bess / wind-bess / smr / large-nuclear). */
export interface BtmSourceRef {
  id: string;
  label: string;
  capacityFactor: string;
  capacityFactorSource: SourceId;
  /** Indicative LCOE, or an honest "market-dependent" where unsourced. */
  lcoe: string;
  lcoeSource?: SourceId;
  leadTime: string;
  dispatchable: string;
  bestFitWorkloads: string;
  note: string;
  noteSource: SourceId;
}

export const BTM_SOURCE_REFERENCE: ReadonlyArray<BtmSourceRef> = [
  {
    id: 'gas',
    label: 'Natural gas (recip / turbine)',
    capacityFactor: '≈60% fleet avg CF (CCGT dispatch avg; distinct from unit availability; site N-1/ELCC design required)',
    capacityFactorSource: 'eiaCapacityFactor',
    lcoe: 'Gas-price dependent',
    leadTime: '~18–36+ mo typical (OEM queue + lateral + air permit dependent; not a site commitment)',
    dispatchable: 'Dispatchable (subject to firm fuel lateral, air permit & OEM queue)',
    bestFitWorkloads: 'Sustained AI training & colocation (subject to site N-1, fuel lateral & air permit)',
    note: 'Continuous dispatchable baseload when firm fuel and permits are secured; multi-GW co-located gas + DC plants emerging (Chevron + GE Vernova).',
    noteSource: 'btmGas',
  },
  {
    id: 'smr',
    label: 'SMR (small modular reactor)',
    capacityFactor: '95%+',
    capacityFactorSource: 'smrNuclear',
    lcoe: 'Omitted (secondary blog ≈$89–102/MWh FOAK / ~$65/MWh NOAK lacks table/page, dollar year & site boundary)',
    lcoeSource: 'smrNuclear',
    leadTime: 'Generally post-2030',
    dispatchable: 'Firm',
    bestFitWorkloads: 'Hyperscale training / colo (timeline-permitting)',
    note: 'Minimal land, 24/7 carbon-free baseload; timeline lags fast AI build-outs.',
    noteSource: 'smrNuclear',
  },
  {
    id: 'large-nuclear',
    label: 'Large nuclear',
    capacityFactor: '≈91%',
    capacityFactorSource: 'eiaCapacityFactor',
    lcoe: 'High capex; market-dependent',
    leadTime: 'Long (decade-scale)',
    dispatchable: 'Firm',
    bestFitWorkloads: 'Hyperscale baseload',
    note: 'Highest firm capacity factor of any source; long lead time.',
    noteSource: 'eiaCapacityFactor',
  },
  {
    id: 'solar-bess',
    label: 'Solar + BESS',
    capacityFactor: '≈23% (solar PV)',
    capacityFactorSource: 'eiaCapacityFactor',
    lcoe: 'Omitted (secondary blog ≈$26–50/MWh pre-firming lacks table/page, dollar year & 24/7 firming boundary)',
    lcoeSource: 'smrNuclear',
    leadTime: 'Short (1–2 yr)',
    dispatchable: 'Intermittent → firmed with BESS',
    bestFitWorkloads: 'Flexible / batch; firmed hybrid for AI',
    note: 'Serving a high-load-factor AI cluster needs heavy overbuild + BESS to cover the ~23% capacity factor.',
    noteSource: 'aiPowerVolatility',
  },
  {
    id: 'wind-bess',
    label: 'Wind + BESS',
    capacityFactor: '≈34% (wind)',
    capacityFactorSource: 'eiaCapacityFactor',
    lcoe: 'Omitted (secondary blog ≈$26–50/MWh pre-firming lacks table/page, dollar year & 24/7 firming boundary)',
    lcoeSource: 'smrNuclear',
    leadTime: 'Short–medium',
    dispatchable: 'Intermittent → firmed with BESS',
    bestFitWorkloads: 'Flexible / batch; firmed hybrid for AI',
    note: 'Diurnal / seasonal variability; firming required for sustained load.',
    noteSource: 'eiaCapacityFactor',
  },
  {
    id: 'grid',
    label: 'Grid',
    capacityFactor: 'n/a (utility supply)',
    capacityFactorSource: 'eiaCapacityFactor',
    lcoe: 'Tariff-dependent',
    leadTime: 'Gen. queue benchmark often >2,100 days (LBNL 2023 gen. context; load service per utility study)',
    dispatchable: 'Firm (grid-dependent)',
    // No review caveat here: bestFitWorkloads renders with cite(noteSource)
    // ([7] smrNuclear), which does not back it (see SOURCES.smrNuclear.backs).
    bestFitWorkloads: 'Baseline for all; can be paired with BTM generation',
    note: 'Long interconnection timelines are the main driver of BTM adoption.',
    noteSource: 'smrNuclear',
  },
  {
    id: 'battery',
    label: 'Battery storage (BESS) — firming layer',
    capacityFactor: 'n/a (storage)',
    capacityFactorSource: 'aiPowerVolatility',
    lcoe: 'Adder to the paired source',
    leadTime: 'Short',
    dispatchable: 'Smoothing / firming',
    bestFitWorkloads: 'AI training & intermittent-source firming (subject to transient study)',
    note: 'Decouples synchronized GPU power swings from the grid and firms intermittent renewables.',
    noteSource: 'aiPowerVolatility',
  },
];
